import React, { useState, useMemo } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import DiscountModal from './DiscountModal';
import JournalEntryModal from './JournalEntryModal';
import { buildJournalFormData } from '../../utils/buildJournalFormData';

/**
 * DiscountPanel
 *
 * - Shows PASS + FAIL discount logic checks
 * - Journal Entry button ONLY for FAIL
 * - Uses buildJournalFormData to construct journal state
 */
const DiscountPanel = ({
  visit,
  discounts = [],
  journalIndex,          
  refreshVisitDetails,
}) => {
  const [infoItem, setInfoItem] = useState(null);
  const [openJournal, setOpenJournal] = useState(false);
  const [journalState, setJournalState] = useState(null);
  
  const sortedChecks = useMemo(() => {
    const logicChecks = discounts.flatMap(discount => {
      const gross = discount.gross_revenue || 0;
      const net = discount.net_revenue || 0;

      const discountAmount = discount.logic_checks?.length
        ? Math.max(...discount.logic_checks.map(lc => lc.price || 0))
        : gross - net;

      const base = {
        parent: {
          staffid: discount.staffid,
          datetime: discount.datetime,
          invoiceitemid: discount.invoiceitemid,
          gross_revenue: gross,
          net_revenue: net,
          discount_types: discount.discount_types,
          reason: discount.reason,
        },
        discountAmount,
      };

      if (discount.logic_checks?.length) {
        return discount.logic_checks.map(lc => ({
          ...lc,
          ...base,
        }));
      }

      return [{
        title: 'Discount Applied',
        result: null,
        color: '#ccc',
        ...base,
      }];
    });

    return logicChecks.sort((a, b) => {
      if (a.result !== b.result) return a.result === 'FAIL' ? -1 : 1;
      return (a.priority || 0) - (b.priority || 0);
    });
  }, [discounts]);
  
  const normalizeDateKey = (date) => {
    if (!date) return null;
  
    let d;
    if (typeof date === 'string' && !date.includes('Z') && !date.includes('+')) {
      d = new Date(date.replace(' ', 'T') + 'Z');
    } else {
      d = new Date(date);
    }
  
    return d.toISOString().slice(0, 19);
  };
  
  const hasDiscountJournal = (item) => {
    const invoiceitemid = item?.parent?.invoiceitemid;
    const completed_date = item?.parent?.datetime;

    if (!invoiceitemid || !completed_date) return false;

    const key = [
      visit.unique_id,
      invoiceitemid,
      normalizeDateKey(completed_date),
    ].join('|');

    return journalIndex?.byInvoiceKey?.has(key);
  };

  const handleJournalClick = (item) => {
    const built = buildJournalFormData({
      journalType: 'discount',
      visit,
      discountItem: item,
    });

    setJournalState(built);
    setOpenJournal(true);
  };

  const handleCloseJournal = () => {
    setOpenJournal(false);
    setJournalState(null);
  };

  return (
    <>
      <div className="discounts-panel">
        <h4 style={{ marginBottom: '12px' }}>
          Discounts{' '}
          <span style={{ fontSize: '0.9rem', color: '#666' }}>
            — Total: <strong>${visit.total_discounts}</strong>
            {visit.failed_discounts > 0 && (
              <>
                {' '}| Failed:{' '}
                <strong style={{ color: 'red' }}>
                  ${visit.failed_discounts.toFixed(2)}
                </strong>
              </>
            )}
          </span>
        </h4>

        {sortedChecks.length === 0 ? (
          <p>No discounts or logic checks available.</p>
        ) : (
          sortedChecks.map((item, idx) => (
            <div
              key={idx}
              className="charge-capture-card"
              style={{
                borderLeft: `4px solid ${item.color}`,
                padding: '8px 12px',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}
            >

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 'bold' }}>
                  {item.title || 'Discount Applied'}
                </div>

                <div style={{ fontSize: '0.85rem', color: '#666' }}>
                  Type: {item.parent?.discount_types?.replace(/,\s*$/, '')}                  
                </div>

                <div style={{ fontSize: '0.85rem', color: '#666' }}>
                  Reason:{item.parent?.reason}
                </div>

                <div style={{ fontSize: '0.85rem', color: '#666' }}>
                  Discount: ${item.discountAmount.toFixed(2)}
                  {item.parent?.gross_revenue
                    ? ` (${(
                        (item.discountAmount / item.parent.gross_revenue) *
                        100
                      ).toFixed(1)}%)`
                    : ''}
                </div>

                <div style={{ fontSize: '0.8rem', color: '#555' }}>
                  Invoice Item: {item.parent.invoiceitemid} — Gross $
                  {item.parent.gross_revenue.toFixed(2)} | Net $
                  {item.parent.net_revenue.toFixed(2)}
                </div>

                <div style={{ fontSize: '0.75rem', color: '#999' }}>
                  {new Date(item.parent.datetime).toLocaleString()}
                </div>
              </div>

              {item.result && (
                <span
                  className={item.result === 'FAIL' ? 'fail' : 'pass'}
                  style={{ fontWeight: 'bold' }}
                >
                  {item.result}
                </span>
              )}

              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <Tooltip title="More Info">
                  <IconButton
                    size="small"
                    onClick={() => setInfoItem(item)}
                  >
                    <HelpOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                {item.result === 'FAIL' && (() => {
                  const journalExists = hasDiscountJournal(item);

                  return (
                    <Tooltip
                      title={journalExists ? 'Journal already exists' : 'Journal Entry'}
                    >
                      <IconButton
                        size="small"
                        onClick={
                          journalExists
                            ? undefined
                            : () => handleJournalClick(item)
                        }
                        sx={{
                          padding: '2px',
                          width: '24px',
                          height: '24px',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          backgroundColor: '#f5f5f5',
                          pointerEvents: journalExists ? 'none' : 'auto',
                          cursor: journalExists ? 'default' : 'pointer',
                          '&:hover': {
                            backgroundColor: '#e5e7eb',
                          },
                        }}
                      >
                        {journalExists ? (
                          <CheckCircleIcon
                            fontSize="small"
                            className="icon-completed"
                          />
                        ) : (
                          <EditIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  );
                })()}
              </div>
            </div>
          ))
        )}
      </div>

      <DiscountModal
        open={!!infoItem}
        onClose={() => setInfoItem(null)}
        item={infoItem}
      />

      <JournalEntryModal
        open={openJournal}
        onClose={handleCloseJournal}
        onSuccess={handleCloseJournal}
        journal={journalState}
        refreshVisitDetails={refreshVisitDetails}      
      />
    </>
  );
};

export default DiscountPanel;